import {Component, ViewChild} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {EDialogOpenMode} from '../../../../sdk/src/enums/dialog-open-mode.enum';
import {PrimeNgImportsModule} from '../../../../sdk/src/prime-ng-imports';
import {firstValueFrom, takeUntil} from 'rxjs';
import {CpmCObjectStoreEntryProxyApiModel} from '../../../../gen/cpm-c-api-ts/src/models/c-customer/cpmc-object-store-entry-proxy.api-model';
import {CpmCObjectStoreEntryProxyApiService} from '../../../../gen/cpm-c-api-ts/src/services/c-customer/cpmc-object-store-entry-proxy.api-service';
import {CpmCObjectStoreEntryProxyCreateApiSchema} from '../../../../gen/cpm-c-api-ts/src/schemas/c-customer/cpmc-object-store-entry-proxy.api-schemas';
import {
    ECpmCObjectStoreEntryProxyTypeApiEnum,
    RECpmCObjectStoreEntryProxyTypeApiEnum
} from '../../../../gen/cpm-c-api-ts/src/enums/c-customer/cpmc-object-store-entry-proxy.api-enums';
import {JSONSchema7} from 'json-schema';
import {JsonSchemaFormComponent} from '../../../../sdk/src/components/json-schema-form/json-schema-form.component';
import {ObjectStoreEntryUtil} from '../../utils/object-store-entry.util';
import {TreeNode} from 'primeng/api';
import {ActionJobsUtils} from '../../utils/action-jobs.utils';
import {StringUtils} from '../../../../sdk/src/utils/string.util';
import {TreeUtils} from '../../../../sdk/src/utils/tree.utils';
import {SdkImportsModule} from '../../../../sdk/src/sdk-imports';
import {JsonUtils} from '../../../../sdk/src/utils/json.utils';
import {JobsSpecificationCaching} from '../../caching/jobs-specification.caching';
import {CpmAEAllPluginSpecApiSchema} from '../../../../gen/cpm-ae-api-ts/src/schemas/ae-global/cpmae-plugins.api-schemas';
import {JsonSchemaUtils} from '../../../../sdk/src/utils/json-schema.utils';
import {isEqual} from 'lodash';

export class NewEditObjectStoreEntryModalConfig {
    constructor(
        public linkObjectType?: string,
        public linkObjectUUID?: string,
        public existing?: CpmCObjectStoreEntryProxyApiModel,
        public forceExistingRefresh = false,
        public version?: number,
        public canRevert?: () => boolean,
        public entryKey?: string
    ) {}
}

export class NewEditObjectStoreEntryModalResult {
    constructor(public item?: CpmCObjectStoreEntryProxyApiModel) {}
}

/**
 * New/Edit ObjectStoreEntry modal
 */
@Component({
    selector: 'app-new-edit-object-store-entry-modal',
    templateUrl: './new-edit-object-store-entry-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule],
    providers: []
})
export class NewEditObjectStoreEntryModalComponent extends IBaseCRUModalComponent<
    CpmCObjectStoreEntryProxyApiModel,
    NewEditObjectStoreEntryModalComponent,
    NewEditObjectStoreEntryModalConfig,
    NewEditObjectStoreEntryModalResult
> {
    protected readonly ECpmCObjectStoreEntryProxyTypeApiEnum = ECpmCObjectStoreEntryProxyTypeApiEnum;

    // ===============================================================
    // ===============================================================

    protected item?: CpmCObjectStoreEntryProxyApiModel;
    protected vtypeSchemaVersion = 0;
    protected vtypeOptions = Object.keys(ECpmCObjectStoreEntryProxyTypeApiEnum).map((key) => ({
        label: StringUtils.toHumanFormatedStr(key),
        value: RECpmCObjectStoreEntryProxyTypeApiEnum[key]
    }));
    protected schemasTreeRoot: TreeNode<string> = {children: []};

    protected specificationAppDetails!: CpmAEAllPluginSpecApiSchema;
    protected vtypeSchema?: JSONSchema7;

    @ViewChild('valueForm')
    protected valueForm?: JsonSchemaFormComponent;
    protected hasValue = false;
    protected valueData?: unknown;
    protected valueValid = false;
    private _initialValueData?: unknown;

    // ===============================================================
    // ===============================================================

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure(
            'ObjectStoreEntryApiModel',
            (model: CpmCObjectStoreEntryProxyApiModel) => model.key ?? '<Unknown object-store-entry>'
        );

        this.canSubmitExtra = () =>
            this.dialogOpenMode === EDialogOpenMode.REVERT || !this.hasValue || this.valueValid;
        this.config.width = '70%';
        this.contentMinHeight = '30rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            key: new FormControl<string | null>(null, [Validators.required]),
            vtype: new FormControl<string | null>(null, [Validators.required]),
            schema: new FormControl<TreeNode | null>(null, []),
            dummy: new FormControl<string | null>(null, [])
        });

        if (this.config.data?.extended?.version) {
            this.updateModalTitle('v' + this.config.data.extended.version);
        }
        this.canRevert = this.config.data?.extended?.canRevert;

        await this.parallelSequencedAsyncCritical([
            {
                m: 'Loading the object store entry...',
                f: async (): Promise<void> => {
                    if (this.dialogOpenMode === EDialogOpenMode.NEW) return;
                    if (!this.config.data!.extended!.forceExistingRefresh) {
                        this.item = this.config.data!.extended!.existing;
                        return;
                    }

                    this.item = await firstValueFrom(
                        CpmCObjectStoreEntryProxyApiService.getObjectStoreEntryProxy(
                            this.config.data!.extended!.linkObjectType!,
                            this.config.data!.extended!.linkObjectUUID!,
                            this.config.data!.extended!.entryKey ?? this.config.data!.extended!.existing!.key
                        )
                    );
                }
            },
            {
                m: 'Loading schemas',
                f: async (): Promise<void> => {
                    this.schemasTreeRoot = await ActionJobsUtils.getTypesTree();
                }
            },
            {
                m: 'Loading active specification',
                f: async (): Promise<void> => {
                    // Retrieve the action‑job specification from the cache.
                    this.specificationAppDetails = (await firstValueFrom(
                        JobsSpecificationCaching.getExecutorActiveSpecificationAppDetails('action')
                    )) as unknown as CpmAEAllPluginSpecApiSchema;

                    if (!this.specificationAppDetails) {
                        throw new Error("No active specification found for jobsType 'Action'");
                    }
                }
            }
        ]);

        if (this.dialogOpenMode !== EDialogOpenMode.NEW) {
            this.loadingStr = 'Loading the object store entry...';

            if (this.item!.value) {
                this.valueData = JsonUtils.deserializeValue(this.item!.value);

                this.hasValue = this.valueData !== undefined && this.valueData !== null;
                if (!this.isCreationMode && this.hasValue) {
                    this._initialValueData = structuredClone(this.valueData);
                }
            }
            if (!this.isCreationMode) {
                this.form.controls['key'].disable();
                this.form.controls['vtype'].disable();
                this.form.controls['schema'].disable();
            }

            const toPatch = {...this.item!};
            delete toPatch.schema;

            this.form.patchValue(toPatch);
            if (this.item!.schema) {
                this.form.controls['schema'].setValue(
                    TreeUtils.findTreeNodeByKey(this.schemasTreeRoot.children!, this.item!.schema!)
                );
            }
        }

        // Compute the initial vtypeSchema based on the patched form values
        await this.onVtypeChanged();

        // Update the form when the type change
        this.form.controls['vtype'].valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(this.onVtypeChanged.bind(this));
        this.form.controls['schema'].valueChanges
            .pipe(takeUntil(this._destroy$))
            .subscribe(this.onSchemaChanged.bind(this));
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * When the key is changed
     */
    protected onKeyChanged() {
        this.form.controls['key'].setValue(StringUtils.toSnakeCase(this.form.controls['key'].value));
    }
    /**
     * When the vtype changed
     */
    protected async onVtypeChanged() {
        if (this.isCreationMode) {
            if (
                this.form.controls['vtype'].value == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT ||
                this.form.controls['vtype'].value == ECpmCObjectStoreEntryProxyTypeApiEnum.OBJECT_ARRAY
            ) {
                this.form.controls['schema'].enable({emitEvent: false});
                this.form.controls['schema'].setValidators([Validators.required]);
                this.form.controls['schema'].updateValueAndValidity({emitEvent: false});
            } else {
                this.form.controls['schema'].setValue(null);
                this.form.controls['schema'].clearValidators();
                this.form.controls['schema'].disable({emitEvent: false});
                this.form.controls['schema'].updateValueAndValidity({emitEvent: false});
            }
        }

        this.form.updateValueAndValidity({emitEvent: false});
        await this.refreshVtypeSchema();
    }
    /**
     * When the schema changed
     */
    protected async onSchemaChanged() {
        await this.refreshVtypeSchema();

        if (this.dialogOpenMode !== EDialogOpenMode.READ_ONLY) this.valueData = null;
    }
    /**
     * Recomputes vtypeSchema and increments vtypeSchemaVersion so Angular
     * destroys and re-creates the JsonSchemaFormComponent (which has no
     * ngOnChanges support) whenever the type or schema selection changes.
     */
    private async refreshVtypeSchema() {
        const schema = await ObjectStoreEntryUtil.getSchemaForVtype(
            this.form.controls['vtype'].value,
            this.form.controls['schema'].value?.key
        );
        this.vtypeSchema = schema
            ? {...schema, definitions: this.specificationAppDetails.type_definitions as Record<string, JSONSchema7>}
            : undefined;
        this.vtypeSchemaVersion++;
    }

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit()) {
            return;
        }

        if (this.dialogOpenMode === EDialogOpenMode.REVERT) {
            if (!this.config.data?.extended?.version) {
                throw new Error('Cannot revert without a version number');
            }
            await this.submitRequest(
                () =>
                    CpmCObjectStoreEntryProxyApiService.revertObjectStoreEntryProxy(
                        this.config.data!.extended!.linkObjectType!,
                        this.config.data!.extended!.linkObjectUUID!,
                        this.item!.key,
                        this.config.data!.extended!.version!
                    ),
                ObjectStoreEntryUtil.getRevertSubmitRequestMessages(
                    this.item!.key,
                    this.config.data!.extended!.version!
                ),
                (resultCRUD: CpmCObjectStoreEntryProxyApiModel) => new NewEditObjectStoreEntryModalResult(resultCRUD)
            );
            return;
        }

        // Store last  value
        if (this.valueForm) this.valueData = this.valueForm.getData();

        // In edit mode, remove unchanged secret leaves and avoid useless no-op update calls.
        if (this.shouldCloseWithoutSubmittingAfterSecretStrip()) {
            this.ref.close();
            return;
        }

        const toSubmit: CpmCObjectStoreEntryProxyCreateApiSchema = {
            vtype: this.form.controls['vtype'].value,
            schema: this.form.controls['schema'].value?.key,
            value: this.hasValue ? JsonUtils.serializeValue(this.valueData) : null,
            overwrite_existing: !this.isCreationMode
        };

        await this.submitRequest(
            () =>
                CpmCObjectStoreEntryProxyApiService.setObjectStoreEntryProxy(
                    this.config.data!.extended!.linkObjectType!,
                    this.config.data!.extended!.linkObjectUUID!,
                    this.form.controls['key'].value,
                    toSubmit
                ),
            this.getSubmitRequestMessages(),
            (resultCRUD: CpmCObjectStoreEntryProxyApiModel) => new NewEditObjectStoreEntryModalResult(resultCRUD)
        );
    }

    /**
     * In edit mode only, strip unchanged `x-is-secret` leaves from the current value payload.
     * If nothing remains after stripping and cleanup, returns true to indicate the modal can
     * be closed without issuing any update request.
     */
    private shouldCloseWithoutSubmittingAfterSecretStrip(): boolean {
        if (this.isCreationMode || !this.hasValue || this.valueData === undefined || this.valueData === null) {
            return false;
        }

        if (!this._initialValueData || !this.vtypeSchema) {
            return false;
        }

        const secretPaths = JsonSchemaUtils.collectSecretLeafPaths(this.vtypeSchema, this.vtypeSchema);
        if (secretPaths.length === 0) {
            return false;
        }

        // Root-level masked marker from API must never be sent back.
        if (secretPaths.some((path) => path.length === 0) && this.valueData === JsonUtils.SECRET_MASKED_MARKER) {
            return true;
        }

        if (secretPaths.some((path) => path.length === 0) && isEqual(this.valueData, this._initialValueData)) {
            return true;
        }

        JsonUtils.stripMaskedSecretMarkersByPaths(
            this.valueData,
            secretPaths.filter((path) => path.length > 0)
        );
        JsonSchemaUtils.stripUnchangedSecretsByPaths(this.valueData, this._initialValueData, secretPaths);
        this.valueData = JsonUtils.stripEmptyObjects(this.valueData);

        return JsonUtils.isEmptyObject(this.valueData);
    }
}
